// FooterMain.js
import React from "react";
import { Link } from "gatsby";
import { 
  RxTwitterLogo, 
  RxLinkedinLogo, 
  RxGithubLogo,
  RxArrowRight,
  RxEnvelopeClosed 
} from 'react-icons/rx';
import RFLogo from "./RFLogo";

const FooterMain = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Product",
      links: [
        { name: "Features", url: "/features" },
        { name: "How it works", url: "/how-it-works" },
        { name: "Pricing", url: "/pricing" },
        { name: "Contact", url: "/contact" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "Blog", url: "/blog" },
        { name: "About Us", url: "/about" },
        { name: "Careers", url: "/careers" },
        { name: "Partners", url: "/partners" },
        { name: "FAQ", url: "/faq" },
      ],
    },
    {
      title: "Resources",
      links: [
        { name: "Resume Tips", url: "/resume-tips" },
        { name: "Cover Letter Tips", url: "/cover-letter-tips" },
        { name: "Job Hunting Tips", url: "/job-hunting-tips" },
        { name: "Interview Tips", url: "/interview-tips" },
      ],
    },
  ];

  const socialLinks = [
    { name: "Twitter", icon: <RxTwitterLogo className="w-5 h-5" />, url: "https://twitter.com/resumeflex" },
    { name: "LinkedIn", icon: <RxLinkedinLogo className="w-5 h-5" />, url: "https://linkedin.com/company/resumeflex" },
  ];

  return (
    <footer className="bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter Section */}
        <div className="py-12 border-b border-neutral-100">
          <div className="max-w-2xl">
            <h3 className="text-2xl font-bold text-neutral-900 mb-2">
              Stay updated
            </h3>
            <p className="text-neutral-600 mb-6">
              Get the latest news about resume trends and career tips delivered to your inbox.
            </p>
            <form className="flex gap-4">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-colors"
                />
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                Subscribe
                <RxArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Logo and Social Section */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="inline-block">
              <RFLogo color="#4F46E5" className="h-8" />
            </Link>
            <p className="text-neutral-600 text-sm">
              AI-powered platform streamlining job applications with smart document generation and personalized optimization.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-neutral-50 text-neutral-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          {footerSections.map((section, index) => (
            <div key={index} className="space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.url}
                      className="text-sm text-neutral-600 hover:text-indigo-600 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-neutral-900">
              Contact
            </h3>
            <div className="space-y-3">
              <a
                href="mailto:hello@resumeflex.com"
                className="text-sm text-neutral-600 hover:text-indigo-600 transition-colors flex items-center gap-2"
              >
                contact@resumeflex.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-neutral-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-neutral-600">
            &copy; {currentYear} ResumeFlex. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-sm text-neutral-600 hover:text-indigo-600 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-sm text-neutral-600 hover:text-indigo-600 transition-colors">
              Terms of Service
            </Link>
            <Link to="/refund-policy" className="text-sm text-neutral-600 hover:text-indigo-600 transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterMain;